/** Client for the control plane.
 *
 * Everything is session-scoped and goes through the gateway — the browser has no
 * route to the workspace container, by design. That is what lets terminal bytes be
 * recorded in transit, somewhere the candidate cannot reach.
 *
 * Every path here is root-relative because the control plane serves this bundle
 * itself. There is no configurable base URL: the `__Host-` session cookie the
 * WebSockets authenticate with is only sent same-origin, so a bundle pointed at
 * another origin could not talk to it anyway. Dev is same-origin too — Vite
 * proxies `/api` — which keeps the one shape in both places.
 */

/** Absolute `ws:`/`wss:` URL for a root-relative path.
 *
 * `new URL` rejects a relative string without a base, and the scheme must track
 * the page's: a `wss:` page opening a `ws:` socket is blocked as mixed content.
 */
function wsUrl(path: string): URL {
  const url = new URL(path, window.location.href);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url;
}

export interface Node {
  path: string;
  name: string;
  is_dir: boolean;
  size: number | null;
}

export type AssistMode = "off" | "chat" | "assist";

export interface Proposal {
  action_id: string;
  tool: "apply_patch" | "run_command";
  summary: string;
  path?: string | null;
  cmd?: string | null;
  diff?: string | null;
  lines_added?: number | null;
  lines_removed?: number | null;
}

export interface AssistTurn {
  role: string;
  content: unknown;
  at: string;
}

export interface AssistState {
  mode: AssistMode;
  model: string | null;
  tokens_used: number;
  tokens_total: number;
  turns: AssistTurn[];
  pending: Proposal[];
}

export interface DecisionResult {
  action_id: string;
  state: string;
  commit_sha?: string | null;
  exit_code?: number | null;
  stdout?: string;
  stderr?: string;
}

export interface Hit {
  path: string;
  line: number;
  column: number;
  text: string;
}

export interface PauseState {
  state: string;
  paused: boolean;
  elapsed_active_ms: number;
}

/** The session as the *candidate* is served it, key for key.
 *
 * The server projects the row through `CandidateSessionOut` rather than blanking
 * fields, so anything missing here is genuinely absent from the response and
 * would render as `undefined`. Keeping that projection in the type is what makes
 * reading an email address out of the editor a compile error rather than a bug
 * somebody notices in a screenshot.
 */
export interface Session {
  id: string;
  project_id: string;
  mode: string;
  agent_mode: string;
  state: string;
  created_at: string;
  next_seq: number;
  /** What counts against the budget: cached input is weighted by its price. */
  agent_tokens_billable: number;
  agent_token_budget: number;
  model: string | null;
  entrypoint_open: string[];
  started_at: string | null;
  paused_at: string | null;
  elapsed_active_ms: number;
  time_budget_minutes: number | null;
}

/** The whole row, which only a staff member is served.
 *
 * The candidate's own email, who set the exercise, the grading result and the
 * link are the reviewer's side of it. `link` is returned once, at creation.
 */
export interface StaffSession extends Session {
  candidate_email: string;
  created_by: string;
  agent_tokens_in: number;
  agent_cache_read_tokens: number;
  agent_cache_write_tokens: number;
  agent_tokens_out: number;
  grading_state: string;
  grading: Record<string, unknown> | null;
  deadline_at: string | null;
  link?: string | null;
}

/** Who the staff app is signed in as, from `GET /api/admin/me`.
 *
 * `source` is shown rather than kept internal: an admin page that silently
 * presents `dev@localhost` as signed in teaches whoever reads it that the staff
 * door works, which is the opposite of what it proves.
 */
export interface StaffIdentity {
  authenticated: boolean;
  email: string | null;
  role: string | null;
  source: "iap" | "development" | null;
  may_delete: boolean;
  /** Whether this instance has a development door at all. Never who is behind
   * it -- production answers `false` and the sign-in is not offered. */
  dev_door: boolean;
}

/** Who the development door would admit, or null where there is no door.
 *
 * `GET /dev/available` is mounted only where the door exists, so a 404 is the
 * other answer and production has no such path.
 */
export async function devDoorEmail(): Promise<string | null> {
  try {
    const response = await fetch(`/dev/available`);
    if (!response.ok) return null;
    return ((await response.json()) as { email: string | null }).email;
  } catch {
    return null;
  }
}

/** A refusal the server made, as opposed to never reaching one.
 *
 * Both arrive as a thrown error and they ask the reader for different things:
 * an expired link is something only an interviewer can reissue, and an
 * unreachable control plane is worth retrying. Without the status the UI has
 * to guess, and it guessed "could not reach the control plane" for both.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw await failure(res);
  return res.json() as Promise<T>;
}

async function failure(res: Response): Promise<ApiError> {
  const body = await res.json().catch(() => null);
  return new ApiError(res.status, body?.detail ?? res.statusText ?? `request failed: ${res.status}`);
}

/** Load the session named in the link.
 *
 * Returns null when the link carries no session. The editor deliberately does not
 * invent one: a session belongs to a named candidate on a chosen project, and a
 * self-created placeholder would put fake rows in the staff list beside real ones.
 */
export async function resolveSession(): Promise<Session | null> {
  const existing = new URLSearchParams(window.location.search).get("session");
  if (!existing) return null;
  return json<Session>(await fetch(`/api/sessions/${existing}`));
}

export interface Project {
  id: string;
  title: string;
  image: string;
}

export interface EventRow {
  seq: number;
  t: string;
  actor: string;
  type: string;
  payload: Record<string, unknown>;
}

/** Staff-side reads. Separate from sessionApi, which is the candidate's surface. */
export const adminApi = {
  /** Who this browser is signed in as, and whether it may delete. */
  async me(): Promise<StaffIdentity> {
    return json<StaffIdentity>(await fetch(`/api/admin/me`));
  },
  async sessions(): Promise<StaffSession[]> {
    return json<StaffSession[]>(await fetch(`/api/sessions`));
  },
  async session(id: string): Promise<StaffSession> {
    return json<StaffSession>(await fetch(`/api/sessions/${id}`));
  },
  async events(id: string): Promise<EventRow[]> {
    return json<EventRow[]>(await fetch(`/api/sessions/${id}/events?limit=100000`));
  },
  async blob(id: string, ref: string): Promise<string> {
    const res = await fetch(`/api/sessions/${id}/blob?ref=${encodeURIComponent(ref)}`);
    if (!res.ok) throw await failure(res);
    return res.text();
  },
  async projects(): Promise<Project[]> {
    return json<Project[]>(await fetch(`/api/projects`));
  },
  /** `created_by` is deliberately absent: the server takes it from the verified
   *  staff identity, so a client cannot record a colleague as the author. */
  async create(body: {
    project_id: string;
    candidate_email: string;
    mode: string;
    agent_mode: string;
  }): Promise<StaffSession> {
    return json<StaffSession>(
      await fetch(`/api/sessions`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      }),
    );
  },
  /** 404 is success here: the session is gone, which is what was asked for.
   *
   * A 403 is not, and it reaches the caller as itself. Hiding the button for an
   * interviewer is an affordance; `require_admin` is the control, and it answers
   * whatever the page chose to draw. */
  async remove(id: string): Promise<void> {
    const res = await fetch(`/api/sessions/${id}`, { method: "DELETE" });
    if (!res.ok && res.status !== 404) throw await failure(res);
  },
  /** Mint another link for a candidate who lost theirs.
   *
   * The token is never stored, so this re-mints rather than retrieves. It does
   * **not** revoke the link already in their inbox: every link for a session
   * works until it expires, which is what makes a take-home resumable from a
   * second machine. Revocation is closing the session. */
  async reissueLink(id: string): Promise<{ link: string; expires_at: string }> {
    return json(await fetch(`/api/sessions/${id}/link`, { method: "POST" }));
  },
  /** Events after `sinceSeq`, for tailing a session that is still running. */
  async eventsSince(id: string, sinceSeq: number): Promise<EventRow[]> {
    return json<EventRow[]>(
      await fetch(`/api/sessions/${id}/events?since_seq=${sinceSeq}&limit=100000`),
    );
  },
  exportUrl(id: string): string {
    return `/api/sessions/${id}/export`;
  },
};

export function sessionApi(sessionId: string) {
  const root = `/api/sessions/${sessionId}`;
  return {
    async tree(path = ""): Promise<Node[]> {
      const res = await fetch(`${root}/files/tree?path=${encodeURIComponent(path)}`);
      return (await json<{ nodes: Node[] }>(res)).nodes;
    },

    /** Every file in the workspace, flat. Build noise already excluded. */
    async listFiles(): Promise<Node[]> {
      const res = await fetch(`${root}/files/list`);
      return (await json<{ files: Node[] }>(res)).files;
    },

    async read(path: string): Promise<string> {
      const res = await fetch(`${root}/files/content?path=${encodeURIComponent(path)}`);
      return (await json<{ content: string }>(res)).content;
    },

    async write(path: string, content: string): Promise<Node> {
      const res = await fetch(`${root}/files/content`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ path, content }),
      });
      return json<Node>(res);
    },

    async search(query: string, regex = false): Promise<Hit[]> {
      const params = new URLSearchParams({ q: query, regex: String(regex) });
      const res = await fetch(`${root}/search?${params}`);
      return (await json<{ hits: Hit[] }>(res)).hits;
    },

    async assistState(): Promise<AssistState> {
      return json<AssistState>(await fetch(`${root}/assist`));
    },

    assistStreamUrl(): string {
      return wsUrl(`${root}/assist/stream`).toString();
    },

    /** Approve or reject one proposed action. Nothing else authorises it. */
    async decide(actionId: string, decision: "approve" | "reject"): Promise<DecisionResult> {
      const res = await fetch(`${root}/assist/actions/${encodeURIComponent(actionId)}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ decision }),
      });
      return json<DecisionResult>(res);
    },

    /** Report something only the browser can witness. Best-effort by nature. */
    async reportEvent(type: string, payload: Record<string, unknown>): Promise<void> {
      await fetch(`${root}/client-events`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ type, payload }),
      });
    },

    /** End the session. Irreversible: no further edits or commands are accepted. */
    /** Re-read this session, for values the server owns like token spend. */
    async session(): Promise<Session> {
      return json<Session>(await fetch(root));
    },

    /** Tell the server this tab is still open. The clock lives server-side. */
    async heartbeat(): Promise<{
      state: string;
      elapsed_active_ms: number;
      budget_ms: number;
      remaining_ms: number | null;
      paused: boolean;
      auto_submitted?: boolean;
    }> {
      return json(await fetch(`${root}/heartbeat`, { method: "POST" }));
    },

    /** Stop the clock and freeze the workspace, or start it again. */
    async pause(): Promise<PauseState> {
      return json<PauseState>(await fetch(`${root}/pause`, { method: "POST" }));
    },

    async resume(): Promise<PauseState> {
      return json<PauseState>(await fetch(`${root}/resume`, { method: "POST" }));
    },

    async submit(): Promise<Session> {
      const res = await fetch(`${root}/submit`, { method: "POST" });
      return json<Session>(res);
    },

    lspUrl(): string {
      return wsUrl(`${root}/lsp`).toString();
    },

    ptyUrl(cols: number, rows: number): string {
      const url = wsUrl(`${root}/pty`);
      url.searchParams.set("cols", String(cols));
      url.searchParams.set("rows", String(rows));
      return url.toString();
    },
  };
}

export type SessionApi = ReturnType<typeof sessionApi>;

/** Monaco's language id for a path, so the editor highlights sensibly. */
export function languageFor(path: string): string {
  const ext = path.split(".").pop()?.toLowerCase() ?? "";
  const byExt: Record<string, string> = {
    py: "python", js: "javascript", jsx: "javascript", ts: "typescript",
    tsx: "typescript", json: "json", md: "markdown", yaml: "yaml", yml: "yaml",
    sh: "shell", toml: "ini", cfg: "ini", ini: "ini", html: "html", css: "css",
    sql: "sql", c: "c", h: "c", cpp: "cpp", hpp: "cpp", rs: "rust", go: "go",
  };
  return byExt[ext] ?? "plaintext";
}
